'use client'
import { useEffect } from 'react'

import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { trackReferralCouponGenerated, trackReferralError } from '@src/telemetry/referral-telemetry'

// Yotpo's Loyalty & Referrals SDK dispatches this on `document` when a referred customer's discount
// coupon is issued. `event.detail.coupon_code` carries the code (which we never forward — count only).
const REFERRED_DISCOUNT_EVENT = 'swell:referred:customer:discount'

/**
 * Referral funnel step 3 (referred-friend coupon), mounted globally so it fires wherever a referred
 * shopper lands — not only on the rewards/refer-and-earn surfaces where `useSwellSDK` runs. Emits
 * `referral_code_generated` (`source: swell_referred_customer`) with only `hasCode`; the coupon code
 * itself is never sent. Telemetry-only: it never touches coupon/referral state.
 */
export const useReferralCouponTelemetry = (): void => {
  useEffect(() => {
    if (!isBrowser()) return undefined

    const handleReferredDiscount = (event: Event): void => {
      const couponCode = (event as CustomEvent<{ coupon_code?: string }>)?.detail?.coupon_code
      if (couponCode) {
        trackReferralCouponGenerated({ source: 'swell_referred_customer', hasCode: true })
      } else {
        trackReferralError('couponGeneration', new Error(`${REFERRED_DISCOUNT_EVENT} fired without coupon_code`), {
          source: 'swell_referred_customer',
        })
      }
    }

    document.addEventListener(REFERRED_DISCOUNT_EVENT, handleReferredDiscount)
    return () => {
      document.removeEventListener(REFERRED_DISCOUNT_EVENT, handleReferredDiscount)
    }
  }, [])
}

export default useReferralCouponTelemetry
